import PageHeader from "@/components/base/PageHeader";
import SectionHeading from "@/components/base/SectionHeading";
import { siteInfo } from "@/mocks/site";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "以用户检索为先",
    description: "站点的每个板块都围绕「用户想找什么」来组织，让信息更容易被找到。",
  },
  {
    icon: "ri-shield-check-line",
    title: "真实与可信",
    description: "内容来源于品牌档案与公开资料，标注时间与来源，避免模糊表述。",
  },
  {
    icon: "ri-links-line",
    title: "开放与协作",
    description: "面向伙伴开放合作能力，共同建设公共资源库，降低信息获取门槛。",
  },
];

const timeline = [
  { year: "2013", title: "品牌成立", text: "三名创始成员在工作室完成第一代产品。" },
  { year: "2016", title: "多产品线成形", text: "从单一产品扩展到基础服务与专业工具。" },
  { year: "2020", title: "服务网络扩张", text: "建立规范化服务流程，覆盖更多地区。" },
  { year: "2024", title: "门户化升级", text: "品牌信息与资源查询整合为统一入口。" },
  { year: "2026", title: "开放合作计划", text: "启动公共资源共建，面向伙伴逐步开放能力。" },
];

const scope = [
  { icon: "ri-book-2-line", title: "品牌内容运营", text: "系统维护品牌介绍、发展历程与企业文化内容。" },
  { icon: "ri-newspaper-line", title: "资讯发布", text: "整理并发布公司新闻、行业动态与媒体报道。" },
  { icon: "ri-archive-2-line", title: "资源整合", text: "汇集白皮书、研究报告与实用工具，支持组合检索。" },
  { icon: "ri-hand-heart-line", title: "合作与共建", text: "与行业机构、合作伙伴共同建设公共资源库。" },
];

export default function About() {
  return (
    <div className="w-full">
      <PageHeader
        breadcrumb={[{ label: "关于我们" }]}
        eyebrow="关于本站"
        title="关于我们"
        description="想要XX 品牌介绍与资源查询平台，致力于为用户提供权威、清晰、易检索的品牌信息与资源服务。"
      />

      <section className="px-4 md:px-8 py-14 md:py-20">
        <div className="mx-auto max-w-6xl grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div className="flex flex-col gap-5">
            <SectionHeading
              eyebrow="网站定位"
              title="让品牌信息更容易被了解"
              description={siteInfo.intro}
            />
            <p className="text-sm md:text-base leading-relaxed text-foreground-600">
              我们面向普通消费者、行业关注者与合作伙伴三类人群，提供品牌介绍、最新资讯与资源查询三项核心服务。通过结构化的内容组织与便捷的检索方式，帮助每一位访问者高效获取所需信息。
            </p>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="font-heading text-xl font-semibold text-foreground-950">
                  {siteInfo.resourceCount}
                </span>
                <p className="mt-1 text-xs text-foreground-500">资源收录数量</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="font-heading text-xl font-semibold text-foreground-950">
                  {siteInfo.visitCount}
                </span>
                <p className="mt-1 text-xs text-foreground-500">累计用户访问</p>
              </div>
            </div>
          </div>
          <div className="h-72 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=Bright%20modern%20open%20office%20with%20collaborative%20desks%20and%20soft%20natural%20light%2C%20minimal%20blue%20gray%20interior%20with%20orange%20accents%2C%20clean%20editorial%20photography&width=1200&height=900&seq=about-team-01&orientation=landscape"
              alt="想要XX 团队办公场景"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="bg-background-100 px-4 md:px-8 py-14 md:py-20">
        <div className="mx-auto max-w-6xl flex flex-col gap-10">
          <SectionHeading
            eyebrow="我们的理念"
            title="三条被长期坚持的原则"
            description="这些原则决定了我们如何组织内容、如何对待信息，以及如何与合作伙伴相处。"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {values.map((item) => (
              <div
                key={item.title}
                className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-md bg-accent-500/15 text-accent-600">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-14 md:py-20">
        <div className="mx-auto max-w-6xl flex flex-col gap-10">
          <SectionHeading eyebrow="发展历程" title="一路走来的重要节点" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {timeline.map((item) => (
              <div
                key={item.year}
                className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="font-heading text-lg font-semibold text-accent-600">
                  {item.year}
                </span>
                <span className="h-px w-8 bg-accent-400" />
                <h3 className="font-heading text-sm font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-500">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary-900 px-4 md:px-8 py-14 md:py-20">
        <div className="mx-auto max-w-6xl flex flex-col gap-10">
          <div className="flex flex-col gap-3">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent-400">
              <span className="w-5 h-px bg-accent-400" />
              业务范围
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-background-50">
              我们提供哪些服务
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {scope.map((item) => (
              <div
                key={item.title}
                className="flex flex-col gap-3 rounded-lg border border-primary-700 bg-primary-800/60 p-5"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-md bg-accent-500/15 text-accent-400">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="font-heading text-sm font-semibold text-background-50">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-primary-200">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}